import { Box, Button, Typography } from '@mui/material';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import { useEffect, useState } from 'react';
import { formatCountdown } from 'app/auth/sessionTimeout';

export interface SessionTimeoutDialogProps {
  open: boolean;
  /** Epoch ms at which the session is logged out. */
  deadline: number;
  /** Countdown reached zero — the guard performs the idle logout. */
  onExpired: () => void;
  /** "Log out" — immediate, user-initiated sign out. */
  onLogout: () => void;
  /** "Continue session" — resets the inactivity timer. */
  onContinue: () => void;
}

// Design tokens — Figma "speedchain_BasicDialog" (node 4260:3869), Material 3.
// Same palette as StepUpIntroDialog (SEE-221).
const COLOR_TITLE = '#3A3B41';
const COLOR_SUPPORT = '#44474F';
const COLOR_PRIMARY = '#193059';
const COLOR_PRIMARY_HOVER = '#142747';
const COLOR_SURFACE = '#E8E7EF';

/**
 * SEE-219: the "Session time out" warning shown shortly before the inactivity
 * soft logout. The countdown is derived from the deadline each tick (not
 * decremented state) so a suspended tab that wakes up shows the true
 * remaining time — or fires onExpired immediately when it already passed.
 */
function SessionTimeoutDialog({
  open,
  deadline,
  onExpired,
  onLogout,
  onContinue,
}: Readonly<SessionTimeoutDialogProps>): JSX.Element {
  const [remainingMs, setRemainingMs] = useState(() => deadline - Date.now());

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setRemainingMs(deadline - Date.now());
    const interval = setInterval(() => {
      const remaining = deadline - Date.now();
      setRemainingMs(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        onExpired();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [open, deadline, onExpired]);

  return (
    <ResponsiveDialog
      open={open}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 312 },
          maxWidth: '100%',
          bgcolor: COLOR_SURFACE,
          borderRadius: '28px',
        },
      }}
    >
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Typography
          component="h2"
          sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 400, color: COLOR_TITLE }}
        >
          Session time out
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.25px', color: COLOR_SUPPORT }}>
          Your session is about to expire. You will be logged out automatically in
        </Typography>
        <Typography
          sx={{
            fontSize: 16,
            lineHeight: '24px',
            fontWeight: 700,
            color: COLOR_TITLE,
            textAlign: 'center',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatCountdown(remainingMs)}
        </Typography>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', mt: '8px' }}
        >
          <Button
            onClick={onLogout}
            variant="text"
            sx={{
              color: COLOR_PRIMARY,
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 600,
              borderRadius: '20px',
              px: '12px',
            }}
          >
            Log out
          </Button>
          <Button
            onClick={onContinue}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: COLOR_PRIMARY,
              color: '#fff',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 600,
              borderRadius: '12px',
              px: '24px',
              '&:hover': { bgcolor: COLOR_PRIMARY_HOVER },
            }}
          >
            Continue session
          </Button>
        </Box>
      </Box>
    </ResponsiveDialog>
  );
}

export default SessionTimeoutDialog;
